import { useModal } from "@ebay/nice-modal-react";
import { Controller, useForm } from "react-hook-form";
import { useMemo } from "react";

import Box from "ds/components/Box";
import Button from "ds/components/Button";
import ComboBox from "ds/components/ComboBox";
import ComboBoxDefaultItem from "ds/components/ComboBox/DefaultItem";
import DrawerBody from "ds/components/Drawer/Body";
import DrawerFooter from "ds/components/Drawer/Footer";
import DrawerFooterActions from "ds/components/Drawer/FooterActions";
import DrawerHeader from "ds/components/Drawer/Header";
import DrawerCloseIcon from "ds/components/DrawerNew/CloseIcon";
import DrawerHeaderTitle from "ds/components/DrawerNew/HeaderTitle";
import { createDrawer, createDrawerTrigger } from "ds/components/DrawerNew/utils";
import Feedback from "ds/components/Feedback";
import useAttachPolicyToIntentProject from "shared/IntentProject/useAttachPolicyToIntentProject";
import { Policy, PolicyType } from "types/generated";
import DrawerForm from "ds/components/DrawerNew/Form";

import { GET_INTENT_PROJECT_POLICIES_REFETCH_QUERIES } from "./constants";

type AttachPolicyFormFields = {
  policyType: PolicyType.Intent;
  policy: string;
};

type IntentProjectPoliciesAttachDrawerProps = {
  entityId: string;
  attachablePolicies: Policy[];
};

const IntentProjectPoliciesAttachDrawer = createDrawer(
  ({ entityId, attachablePolicies }: IntentProjectPoliciesAttachDrawerProps) => {
    const drawer = useModal();
    const attachPolicyForm = useForm<AttachPolicyFormFields>({
      mode: "onChange",
      defaultValues: {
        policyType: PolicyType.Intent,
      },
    });

    const { control, handleSubmit } = attachPolicyForm;

    const { attachPolicy, loading: isAttaching } = useAttachPolicyToIntentProject(
      GET_INTENT_PROJECT_POLICIES_REFETCH_QUERIES
    );

    const policiesOptions = useMemo(() => {
      return attachablePolicies.map((policy) => ({
        value: policy.id,
        label: policy.name,
      }));
    }, [attachablePolicies]);

    const handleClose = () => {
      drawer.hide();
    };

    const handleCancelAttaching = () => {
      handleClose();
    };

    const handleAttach = (formData: AttachPolicyFormFields) => {
      attachPolicy(formData.policy, entityId, () => {
        handleClose();
      });
    };

    const handleFormSubmit = () => {
      handleSubmit(handleAttach)();
    };

    return (
      <DrawerForm isDirty={attachPolicyForm.formState.isDirty} onClose={handleCancelAttaching}>
        <DrawerHeader justify="between">
          <DrawerHeaderTitle title="Attach policy" />
          <DrawerCloseIcon />
        </DrawerHeader>
        <DrawerBody>
          <Box direction="column" gap="x-large">
            <Feedback type="banner" variant="info">
              Only available Intent policies are shown below.
            </Feedback>

            <Controller
              name="policy"
              control={control}
              rules={{ required: "Policy is required." }}
              render={({ field, fieldState }) => (
                <ComboBox
                  label="Select Intent policy"
                  error={fieldState.error?.message}
                  items={policiesOptions}
                  value={field.value}
                  onChange={field.onChange}
                >
                  {(item) => <ComboBoxDefaultItem id={item.value} label={item.label} />}
                </ComboBox>
              )}
            />
          </Box>

          <DrawerFooter>
            <DrawerFooterActions>
              <Button variant="secondary" onPress={handleCancelAttaching} disabled={isAttaching}>
                Cancel
              </Button>
              <Button
                variant="primary"
                type="submit"
                onPress={handleFormSubmit}
                loading={isAttaching}
                disabled={isAttaching}
              >
                Attach
              </Button>
            </DrawerFooterActions>
          </DrawerFooter>
        </DrawerBody>
      </DrawerForm>
    );
  }
);

export const showIntentProjectPoliciesAttachDrawer = createDrawerTrigger(
  IntentProjectPoliciesAttachDrawer
);
